/* eslint-disable @typescript-eslint/no-empty-function */
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { IFormFileDrawingItem } from '@file/projectFile/projectFile.models';
import {
  CircularProgressBar,
  ComponentType,
  Grid,
  Icon,
  Loader,
  Tooltip,
  UseFormReturn,
  removeItemInArray,
} from '@gcl/react-core';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { ETransmittalStatus, ETransmittalType } from '@transmittal/transmittal.constants';
import React, { useCallback, Dispatch, SetStateAction } from 'react';
import { v4 as uuidV4 } from 'uuid';
import { RowState } from '@gcl/react-core/dist/components/Table/UnstyledTableComponents/Layout/VerticalTableLayout';
import { ApiSuccessToast } from '@core/ApiSuccessToast';
import {
  getErrorKeyWithSeperator,
  useDeleteTransmittalItem,
  useFormPrefix,
  useSubmitRow,
} from './transmittalItemTableForm.hooks';
import { ITransmittalItemRowEditingRef } from './transmittalItemTableForm.models';
import { ETransmittalItemTableFormKey } from './transmittalItemTableForm.constants';
import { ETransmittalConfirmationActionType, ETransmittalDetailsFormKeys } from '../transmittalDetailPage.constants';
import { useTransmittalDetailPageConfirmationModal } from '../transmittalDetailPage.hooks';

interface ITransmittalItemTableRowStateButtonsProps {
  setEditingRowCount: React.Dispatch<React.SetStateAction<number>>;
  item: IFormFileDrawingItem;
  transmittalType?: ETransmittalType;
  masterScheduleId: number;
  transmittalId: number;
  transmittalStatus?: ETransmittalStatus;
  rowIndex: number;
  isAnyActionLoading: boolean;
  refRowEditingEvents: React.MutableRefObject<ITransmittalItemRowEditingRef>;
  setRowUploading: (index: number, isUploading: boolean) => void;
  hasDeletePermission: boolean;
  queryKey: QueryKey;
  tableIndex: number;
  useFormMethods: UseFormReturn;
  setRowState: Dispatch<SetStateAction<RowState>> | undefined;
  rowState: RowState | undefined;
}

export const TransmittalItemTableRowStateButtons = ({
  setEditingRowCount,
  rowIndex,
  item,
  transmittalStatus,
  transmittalType = ETransmittalType.DESIGNER,
  masterScheduleId,
  transmittalId,
  isAnyActionLoading,
  refRowEditingEvents,
  setRowUploading,
  hasDeletePermission,
  queryKey,

  tableIndex,
  setRowState,
  rowState,
  useFormMethods,
}: ITransmittalItemTableRowStateButtonsProps) => {
  const queryClient = useQueryClient();

  const { tablePrefix, rowPrefix, rowFormPrefix } = useFormPrefix(tableIndex, rowIndex);

  const [uploadProgress, setUploadProgress] = React.useState<number | undefined>(undefined);

  React.useEffect(() => {
    if (!setRowState) return () => {};

    if (!item?.form?.lastModifiedDateTime) {
      setRowState('Idle');
      return () => {};
    }

    setRowState('Editing');
    return () => {
      if (setRowState) setRowState('Idle');
    };
  }, [JSON.stringify(item.form?.lastModifiedDateTime)]);

  React.useEffect(() => {
    if (rowState === 'Idle') {
      delete refRowEditingEvents.current[rowIndex];
      setEditingRowCount(Object.values(refRowEditingEvents.current).length);
      return () => {};
    }
    if (rowState === 'Editing' || rowState === 'Loading') {
      refRowEditingEvents.current = {
        ...refRowEditingEvents.current,
        [rowIndex]: {
          ...(refRowEditingEvents.current[rowIndex] ?? {}),
          rowState,
          setRowState,
          setUploadProgress,
        },
      };
      setEditingRowCount(Object.values(refRowEditingEvents.current).length);
    }
    return () => {
      if (rowState === 'Editing' || rowState === 'Loading') return;
      delete refRowEditingEvents.current[rowIndex];
      setEditingRowCount(Object.values(refRowEditingEvents.current).length);
    };
  }, [rowState, rowIndex]);

  const {
    onClickSubmitSingleRow,
    mutationUpsertTransmittalItemsAndRevisionsForTables,
    updateCacheFromUpdateTransmittalItemResponse,
  } = useSubmitRow(masterScheduleId, transmittalId, transmittalType, useFormMethods);

  const {
    isLoading: isLoadingUpsertTransmittalItemsAndRevisions,
    isSuccess: isSuccessUpsertTransmittalItemsAndRevisions,
    isError: isErrorUpsertTransmittalItemsAndRevisions,
    error: errorUpsertTransmittalItemsAndRevisions,
    reset: resetUpsertTransmittalItemsAndRevisions,
    data: dataUpsertTransmittalItemsAndRevisions,
  } = mutationUpsertTransmittalItemsAndRevisionsForTables;

  const { useDeleteTransmittalItemMutation, openDeleteTransmittalItemModal, renderDeleteTransmittalItemModal } =
    useDeleteTransmittalItem(transmittalId, item, setRowState);
  const {
    isLoading: isLoadingDeleteTransmittalItem,
    isError: isErrorDeleteTransmittalItem,
    error: errorDeleteTransmittalItem,
    isSuccess: isSuccessDeleteTransmittalItem,
    reset: resetDeleteTransmittalItem,
  } = useDeleteTransmittalItemMutation;

  // onClick
  const onClickSubmit = useCallback(() => {
    onClickSubmitSingleRow(tableIndex, rowIndex, setRowState);
  }, [tableIndex, rowIndex, setRowState]);

  const onClickEdit = () => {
    if (setRowState) setRowState('Editing');
  };
  const onClickCancel = useCallback(async () => {
    useFormMethods.resetField(`${rowFormPrefix}.${ETransmittalItemTableFormKey.docNo}`, { keepDirty: false });
    useFormMethods.resetField(`${rowFormPrefix}.${ETransmittalItemTableFormKey.description}`, {
      keepDirty: false,
    });
    useFormMethods.resetField(`${rowFormPrefix}.${ETransmittalItemTableFormKey.revisionNo}`, {
      keepDirty: false,
    });

    useFormMethods.resetField(rowPrefix, { keepDirty: false, keepError: false, keepTouched: false });

    if (!item.transmittalItem) {
      // cancel new transmittal item
      const oldFormValues: IFormFileDrawingItem[] = useFormMethods.getValues(tablePrefix) ?? [];
      if (setRowState) setRowState('Idle');
      useFormMethods.setValue(tablePrefix, removeItemInArray(oldFormValues, rowIndex));
      return;
    }

    if (setRowState) setRowState('Idle');
  }, [JSON.stringify(item), rowIndex]);

  const onClickDeleteTransmittalItem = (e: any) => {
    openDeleteTransmittalItemModal(e);
  };

  const {
    renderConfirmationPortal: renderUploadRevisionWithSameFileNameConfirmationModal,
    openConfirmationPortal: openUploadRevisionWithSameFileNameConfirmationModal,
  } = useTransmittalDetailPageConfirmationModal(onClickSubmit);

  if (transmittalStatus === ETransmittalStatus.APPROVED) return null;

  if (
    item.form?.projectFile?.uploadState === 'uploadingItem' ||
    item.form?.projectFile?.uploadState === 'uploadingRevision'
  )
    return <div>Uploading</div>; // uploading file
  return (
    <>
      <ApiErrorDisplay
        setFormError={useFormMethods.setError}
        errors={[
          {
            error: errorUpsertTransmittalItemsAndRevisions,
            genericError: `Update item failed`,
            keyExtractor: (originalKey: string) => `${rowFormPrefix}.${getErrorKeyWithSeperator(originalKey).fieldKey}`,
            reset: resetUpsertTransmittalItemsAndRevisions,
          },
          {
            error: errorDeleteTransmittalItem,
            genericError: 'Delete item failed',
            reset: resetDeleteTransmittalItem,
          },
        ]}
        callbackAfterToast={() => {
          if (setRowState) setRowState('Editing');
          setRowUploading(rowIndex, false);
        }}
      />
      <ApiSuccessToast
        successes={[
          {
            isSuccess: isSuccessDeleteTransmittalItem,
            message: 'Delete item success',
            reset: resetDeleteTransmittalItem,
          },
          { isSuccess: isSuccessUpsertTransmittalItemsAndRevisions, reset: resetUpsertTransmittalItemsAndRevisions },
        ]}
        callbackAfterToasted={() => {
          setRowUploading(rowIndex, false);

          if (isSuccessDeleteTransmittalItem) {
            let tableValues = useFormMethods.getValues(`${tablePrefix}`) as IFormFileDrawingItem[];
            tableValues = tableValues.map((rowValues, rowValueIndex) => {
              return {
                ...rowValues,
                form: {
                  ...rowValues.form,
                  lastModifiedDateTime: refRowEditingEvents.current[rowValueIndex] ? Date.now() : undefined,
                },
              } as IFormFileDrawingItem;
            });
            tableValues = removeItemInArray(tableValues, rowIndex);

            useFormMethods.setValue(`${tablePrefix}`, tableValues);
            useFormMethods.setValue(ETransmittalDetailsFormKeys.DOCUMENTS, [
              ...(useFormMethods.getValues(ETransmittalDetailsFormKeys.DOCUMENTS) ?? []),
            ]);
            if (setRowState) setRowState('Idle');

            queryClient.invalidateQueries(queryKey);

            return;
          }

          useFormMethods.setValue(`${rowFormPrefix}.${ETransmittalItemTableFormKey.lastModifiedDateTime}`, undefined);

          updateCacheFromUpdateTransmittalItemResponse(dataUpsertTransmittalItemsAndRevisions?.items);

          // queryClient.invalidateQueries(queryKey);
          if (setRowState) setRowState('Idle');
        }}
      />
      <Grid container columnSpacing={2}>
        <Grid item>
          {rowState === 'Idle' && (
            <>
              {item.transmittalItem?.inUse && (
                <Tooltip overlay="Edit" id="edit" key="edit" placement="left">
                  <Icon
                    data-testid={`edit-transmittal-item-btn-table-${tableIndex}-row-${rowIndex}`}
                    iconName="Pencil"
                    className="gcl-ic-btn-lg gcl-fc-primary-400"
                    onClick={onClickEdit}
                  />
                </Tooltip>
              )}
              {hasDeletePermission && item.form?.projectFile?.uploadState !== 'error' && (
                <Tooltip
                  overlay="Delete transmittal item"
                  id="delete-transmittal-item"
                  key="delete-transmittal-item"
                  placement="left"
                >
                  <Icon
                    data-testid={`delete-transmittal-item-btn-table-${tableIndex}-row-${rowIndex}`}
                    iconName="Trash"
                    className="gcl-ic-btn-lg gcl-fc-danger-400 gcl-ml-xs"
                    onClick={onClickDeleteTransmittalItem}
                  />
                </Tooltip>
              )}
            </>
          )}
          {rowState === 'Idle' && item.form?.projectFile?.uploadState === 'error' && (
            <Tooltip overlay="Cancel" id="cancel" key="cancel" placement="left">
              <Icon iconName="XCircle" className="gcl-ic-btn-lg gcl-fc-danger-400 gcl-ml-xs" onClick={onClickCancel} />
            </Tooltip>
          )}
          {rowState === 'Editing' && (
            <>
              <Tooltip overlay="Submit" id="submit" key="submit" placement="left">
                <Icon
                  data-testid="submit-drawing"
                  iconName="CheckCircle"
                  className="gcl-ic-btn-lg gcl-mr-md gcl-gems-transmittal-item-file-display-check-btn"
                  onClick={(e) => {
                    openUploadRevisionWithSameFileNameConfirmationModal(
                      e,
                      ETransmittalConfirmationActionType.save,
                      useFormMethods,
                      tableIndex,
                      rowIndex,
                    );
                  }}
                />
              </Tooltip>
              <Tooltip overlay="Cancel" id="cancel" key="cancel">
                <Icon
                  iconName="XCircle"
                  className="gcl-ic-btn-lg gcl-gems-transmittal-item-file-display-x-btn"
                  onClick={onClickCancel}
                />
              </Tooltip>
            </>
          )}
          {rowState === 'Loading' && uploadProgress !== undefined && (
            <>
              <Tooltip
                overlay={item.transmittalItem ? 'Updating' : 'Creating'}
                id={`gcl-gems-transmittal-item-table-form-${item?.form?.uuid || uuidV4()}-tooltip`}
              >
                <CircularProgressBar
                  value={uploadProgress || 0}
                  className="gcl-gems-transmittal-item-table-form-upload-progress-bar"
                />
              </Tooltip>
            </>
          )}
          {rowState === 'Loading' && uploadProgress === undefined && (
            <Loader small type={ComponentType.Primary} text="" />
          )}
        </Grid>
      </Grid>
      {renderDeleteTransmittalItemModal(
        isLoadingDeleteTransmittalItem,
        isErrorDeleteTransmittalItem,
        isSuccessDeleteTransmittalItem,
      )}
      {renderUploadRevisionWithSameFileNameConfirmationModal(
        isLoadingUpsertTransmittalItemsAndRevisions,
        isErrorUpsertTransmittalItemsAndRevisions,
        isSuccessUpsertTransmittalItemsAndRevisions,
      )}
    </>
  );
};
